import React, { useEffect, useRef, useState } from "react";
import { Copy, Check, Globe, UserPlus, Download } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MarketingMaterials } from "@/components/marketing/MarketingMaterials";

export default function AgentLink() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => { api.get("/agent/qr").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="text-gray-400">Caricamento…</div>;

  const universalLink = data.universal_link || data.owner_link;
  const agentLink = data.agent_link;

  const copy = (key, value) => {
    navigator.clipboard.writeText(value);
    setCopied(key);
    toast.success("Link copiato");
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="space-y-8 fade-slide max-w-5xl" data-testid="marketing-lead-page">
      <div>
        <h1 className="text-3xl font-bold text-[#0A0A0A]">Marketing &amp; Lead</h1>
        <p className="text-gray-500">Il tuo link referral e i materiali pronti per portare nuovi proprietari in tutte le app ValutaGenio.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-6">

      <LinkBlock
        icon={<Globe size={18} />}
        title="Link per proprietari"
        qrLabel="Link per proprietari"
        description="Questo link funziona per tutte le app ValutaGenio. Chi lo usa viene assegnato a te in qualsiasi parte del sistema."
        link={universalLink}
        filename="valutagenio-qr-universale.png"
        testid="universal"
        copied={copied === "universal"}
        onCopy={() => copy("universal", universalLink)}
      />

      <LinkBlock
        icon={<UserPlus size={18} />}
        title="Invita un collega agente"
        qrLabel="Per agenti"
        description="Quando un agente si registra dal tuo link, entra nella tua rete a tre generazioni. Bronzo 1–25: 30% sulla prima generazione. Oro 26–50: 50% sulla prima + 5% sulla seconda. Diamante 51+: 70% + 5% seconda + 3% terza."
        link={agentLink}
        filename="valutagenio-qr-agenti.png"
        testid="agent"
        copied={copied === "agent"}
        onCopy={() => copy("agent", agentLink)}
      />
      </div>
      <MarketingMaterials />
    </div>
  );
}

function LinkBlock({ icon, title, qrLabel, description, link, filename, testid, copied, onCopy }) {
  const qrRef = useRef(null);

  const download = () => {
    const node = qrRef.current?.querySelector("canvas");
    if (!node) return;
    const url = node.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("QR scaricato");
  };

  return (
    <div className="vg-card p-6" data-testid={`link-block-${testid}`}>
      <div className="flex items-center gap-2 mb-2 text-[#B40101]">
        {icon}
        <h3 className="font-bold text-[#0A0A0A]">{title}</h3>
      </div>
      <p className="text-sm text-gray-500 mb-4">{description}</p>

      <div className="flex gap-2 mb-5">
        <input
          className="vg-input text-sm font-mono"
          readOnly
          value={link}
          data-testid={`link-input-${testid}`}
        />
        <button
          className="vg-btn-primary px-4"
          onClick={onCopy}
          data-testid={`link-copy-${testid}`}
        >
          {copied ? <Check size={16} /> : <Copy size={16} />}
        </button>
      </div>

      <div className="flex flex-col items-center gap-3 pt-4 border-t border-gray-100">
        <div
          ref={qrRef}
          className="p-3 bg-white rounded-[12px] border border-gray-200"
          data-testid={`qr-${testid}`}
        >
          <QRCodeCanvas
            value={link}
            size={180}
            level="M"
            includeMargin={false}
            fgColor="#404040"
            bgColor="#FFFFFF"
          />
        </div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{qrLabel}</p>
        <button
          className="vg-btn-secondary flex items-center gap-2 px-4 py-2 text-sm"
          onClick={download}
          data-testid={`qr-download-${testid}`}
        >
          <Download size={14} />
          Scarica QR
        </button>
      </div>
    </div>
  );
}
